/**
 * CommandPalette — the admin's ⌘K launcher.
 *
 * Press ⌘K (or Ctrl+K) anywhere in the admin to open. Type to search across
 * users, trips, bookings, plan requests and contacts (via /admin/search), or
 * jump to any admin page. Arrow keys to move, Enter to go.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, Box, TextField, List, ListItemButton, ListItemIcon, ListItemText,
  Typography, Chip, InputAdornment, CircularProgress, useTheme, alpha,
} from '@mui/material';
import {
  Search, Person, Luggage, BookOnline, Explore, ContactMail, Dashboard,
  Payment, RateReview, People, BarChart, Email, KeyboardReturn,
} from '@mui/icons-material';
import adminService from '../../services/adminService';

const TYPE_META = {
  user: { icon: <Person />, color: '#1976D2', label: 'User' },
  trip: { icon: <Luggage />, color: '#2E7D32', label: 'Trip' },
  booking: { icon: <BookOnline />, color: '#C24E00', label: 'Booking' },
  plan_request: { icon: <Explore />, color: '#7B1FA2', label: 'Request' },
  contact: { icon: <ContactMail />, color: '#0097A7', label: 'Contact' },
};

// Static quick-nav targets, filtered by the typed query.
const PAGES = [
  { label: 'Dashboard', path: '/admin/dashboard', icon: <Dashboard /> },
  { label: 'Trips', path: '/admin/trips', icon: <Luggage /> },
  { label: 'Bookings', path: '/admin/bookings', icon: <BookOnline /> },
  { label: 'Trip Requests', path: '/admin/plan-requests', icon: <Explore /> },
  { label: 'Payments', path: '/admin/payments', icon: <Payment /> },
  { label: 'Reviews', path: '/admin/reviews', icon: <RateReview /> },
  { label: 'Contacts', path: '/admin/contacts', icon: <ContactMail /> },
  { label: 'Newsletter', path: '/admin/newsletter', icon: <Email /> },
  { label: 'Users', path: '/admin/users', icon: <People /> },
  { label: 'Analytics', path: '/admin/analytics', icon: <BarChart /> },
];

const CommandPalette = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Global ⌘K / Ctrl+K to toggle.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const pageMatches = PAGES.filter((p) => !q || p.label.toLowerCase().includes(q.toLowerCase()));

  // Debounced remote search.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await adminService.search(q.trim());
        setResults(res.results || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  // Flat, ordered list of everything selectable (pages first, then hits).
  const items = [
    ...pageMatches.map((p) => ({ kind: 'page', ...p })),
    ...results.map((r) => ({ kind: 'hit', ...r })),
  ];

  useEffect(() => { setActive(0); }, [q, results.length]);

  const choose = useCallback((item) => {
    if (!item) return;
    setOpen(false);
    setQ('');
    navigate(item.kind === 'page' ? item.path : item.path);
  }, [navigate]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(items[active]); }
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
      PaperProps={{ sx: { borderRadius: 3, width: 640, maxWidth: '92vw', position: 'fixed', top: 80, m: 0 } }}
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
    >
      <Box sx={{ p: 1.5, borderBottom: `1px solid ${theme.palette.grey[200]}` }}>
        <TextField
          inputRef={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          fullWidth
          placeholder="Search users, trips, bookings, requests, contacts…  or jump to a page"
          variant="standard"
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
            endAdornment: loading ? <CircularProgress size={18} /> : (
              <Chip label="ESC" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.6rem' }} />
            ),
            sx: { fontSize: '1.05rem', px: 0.5 },
          }}
        />
      </Box>

      <List sx={{ maxHeight: 420, overflowY: 'auto', py: 0.5 }}>
        {items.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">
              {q.trim().length >= 2 ? 'No matches.' : 'Type at least 2 characters to search.'}
            </Typography>
          </Box>
        )}
        {pageMatches.length > 0 && (
          <Typography variant="overline" sx={{ px: 2, color: 'text.secondary' }}>Pages</Typography>
        )}
        {items.map((item, i) => {
          const meta = item.kind === 'hit' ? TYPE_META[item.type] : null;
          const isActive = i === active;
          // Section header before the first hit.
          const firstHit = item.kind === 'hit' && (i === 0 || items[i - 1].kind === 'page');
          return (
            <React.Fragment key={`${item.kind}-${item.id ?? item.path}-${i}`}>
              {firstHit && (
                <Typography variant="overline" sx={{ px: 2, pt: 1, display: 'block', color: 'text.secondary' }}>
                  Results
                </Typography>
              )}
              <ListItemButton
                selected={isActive}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(item)}
                sx={{
                  mx: 1, borderRadius: 2,
                  '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: meta ? meta.color : theme.palette.text.secondary }}>
                  {item.kind === 'page' ? item.icon : meta?.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.kind === 'page' ? item.label : item.title}
                  secondary={item.kind === 'hit' ? item.subtitle : null}
                  primaryTypographyProps={{ fontWeight: 600, noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
                {meta && <Chip label={meta.label} size="small" sx={{ height: 20, fontSize: '0.62rem', bgcolor: alpha(meta.color, 0.12), color: meta.color, fontWeight: 700 }} />}
                {isActive && <KeyboardReturn sx={{ fontSize: 16, ml: 1, color: 'text.disabled' }} />}
              </ListItemButton>
            </React.Fragment>
          );
        })}
      </List>
    </Dialog>
  );
};

export default CommandPalette;
